import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Stack, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Button } from '@/components/button/button.tsx';
import { useDialogStore } from '@/components/dialogs';
import { LabelValueGrid } from '@/components/label/label_value_grid.tsx';
import { RatioGauge } from '@/components/label/ratio_gauge.tsx';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import { sumOpenPositionsEquity } from '@/feature/terminal_tables/position/positionUtils.ts';
import { PositionStatus } from '@/feature/terminal_tables/position/types.ts';
import { useTokenFormatter } from '@/hooks/useTokenFormatter.ts';
import { bigIntToBig, bigToBigInt, stringToBig } from '@/utils/bigUtils.ts';
import useAppStore, { useAccountBalance, useLockedBalance, useTotalAccountBalance } from '../../store/rootStore.ts';
import { useDepositButtonText } from './hooks/useDepositButtonText.ts';

interface UserDataField {
  key: keyof UserData;
  label: MessageDescriptor;
  tooltip: MessageDescriptor;
  showGauge?: boolean;
  suffix?: string;
}

interface UserData {
  marginRatio?: number;
  maintenanceMargin?: string;
  totalAccountValue?: string;
  availableBalance?: string;
  lockedBalance?: string;
  unrealizedPnL?: React.ReactNode;
}

const USER_DATA_FIELDS: UserDataField[] = [
  // {
  //   key: 'marginRatio',
  //   label: 'Margin Ratio',
  //   showGauge: true,
  //   suffix: '%',
  // },
  // {
  //   key: 'maintenanceMargin',
  //   label: 'Maintenance Margin',
  //   tooltip: '',
  // },
  {
    key: 'totalAccountValue',
    label: msg({ comment: 'Account panel row label', message: 'Account Value' }),
    tooltip: msg({
      comment: 'Account panel tooltip: account value',
      message: 'The total value of assets in your account, including both unrealized PnL and available balance',
    }),
  },
  {
    key: 'availableBalance',
    label: msg({ comment: 'Account panel row label', message: 'Available Balance' }),
    tooltip: msg({
      comment: 'Account panel tooltip: available balance',
      message: 'Amount available to open and maintain positions, or withdraw',
    }),
  },
  // {
  //   key: 'lockedBalance',
  //   label: 'Locked Balance',
  //   tooltip: '',
  // },
  {
    key: 'unrealizedPnL',
    label: msg({ comment: 'Account panel row label', message: 'Unrealized PnL' }),
    tooltip: msg({
      comment: 'Account panel tooltip: unrealized PnL',
      message: 'Your current profit or loss. Finalized when position closes.',
    }),
  },
];

export const UserStatusPanel = React.memo(() => {
  const { t } = useLingui();
  const isAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const isAuthenticated = useAppStore((state) => state.isAtLeastAuthenticated());
  const token = useAppStore((state) => state.exchange?.token);
  const accountBalance = useAccountBalance();
  const totalBalance = useTotalAccountBalance();
  const lockedBalance = useLockedBalance();
  const positions = usePositionStore((state) => state.positions);
  const markets = useAppStore((state) => state.markets);
  const formatToken = useTokenFormatter(token);
  const depositButtonText = useDepositButtonText();

  const openPositions = useMemo(() => positions.filter((p) => p.status === PositionStatus.open), [positions]);

  const openDialog = useDialogStore((state) => state.openDialog);

  const handleDepositClick = useCallback(() => openDialog('depositCollateralDialog'), [openDialog]);
  const handleWithdrawClick = useCallback(() => openDialog('withdrawDialog'), [openDialog]);

  const userData = useMemo<UserData>(() => {
    if (!isAuthorized || !token) return {};

    const unrealizedPnL = openPositions.reduce((sum, p) => sum.plus(p.pnl || 0), stringToBig(0));

    const maintenanceMargin = openPositions.reduce((sum, p) => {
      const market = markets.get(p.marketId);
      if (market?.maintenanceMarginFraction != null && market.maintenanceMarginFraction > 0) {
        const positionMaintMargin = stringToBig(p.size).times(p.markPrice).div(market.maintenanceMarginFraction);
        return sum.plus(positionMaintMargin);
      }
      return sum;
    }, stringToBig(0));

    // Account Value = balance + open-position equity.
    const marginBalance = bigIntToBig(totalBalance, token.decimals).plus(sumOpenPositionsEquity(openPositions));

    let marginRatio: number | undefined;
    if (marginBalance.gt(0)) {
      marginRatio = maintenanceMargin.div(marginBalance).times(100).round(2).toNumber();
    }

    const isNegativePnL = unrealizedPnL.lt(0);
    const isPositivePnL = unrealizedPnL.gt(0);
    const pnlValue = formatToken(bigToBigInt(unrealizedPnL.abs(), token.decimals), { trailingZeroDisplay: 'auto' });
    const pnlColor = isNegativePnL ? 'error.main' : isPositivePnL ? 'success.main' : 'text.primary';

    return {
      marginRatio,
      maintenanceMargin: `$${formatToken(bigToBigInt(maintenanceMargin, token.decimals), { trailingZeroDisplay: 'auto' })}`,
      totalAccountValue: `$${formatToken(bigToBigInt(marginBalance, token.decimals), { trailingZeroDisplay: 'auto' })}`,
      availableBalance: accountBalance != null ? `$${formatToken(accountBalance, { trailingZeroDisplay: 'auto' })}` : '',
      lockedBalance: lockedBalance != null ? `$${formatToken(lockedBalance, { trailingZeroDisplay: 'auto' })}` : '',
      unrealizedPnL: (
        <Box component="span" color={pnlColor}>
          {isNegativePnL ? '-' : ''}${pnlValue}
        </Box>
      ),
    };
  }, [isAuthorized, accountBalance, formatToken, lockedBalance, openPositions, markets, token, totalBalance]);

  const items = useMemo(() => {
    return USER_DATA_FIELDS.map((field) => {
      const value = userData[field.key];

      return {
        label: t(field.label),
        tooltip: t(field.tooltip),
        value:
          field.showGauge && typeof value === 'number' ? <RatioGauge value={value} suffix={field.suffix} /> : value || '--.--',
      };
    });
  }, [userData, t]);

  return (
    <>
      {isAuthorized ? (
        <Stack direction="row" spacing={2} mb={2}>
          <Button variant="outlined" fullWidth onClick={handleDepositClick}>
            {depositButtonText}
          </Button>
          {accountBalance ? (
            <Button variant="outlined" fullWidth onClick={handleWithdrawClick} disabled={!accountBalance}>
              <Trans comment="Button opening the withdraw dialog">Withdraw</Trans>
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Box sx={{ mb: 1, textAlign: 'center' }}>
          <Typography variant="body1">
            {isAuthenticated
              ? t({ comment: 'Account panel prompt when connected but unfunded', message: 'Deposit to start trading' })
              : t({
                  comment: 'Account panel prompt when no wallet connected',
                  message: 'Connect your wallet to start trading',
                })}
          </Typography>
        </Box>
      )}

      <LabelValueGrid items={items} />
    </>
  );
});

UserStatusPanel.displayName = 'UserStatusPanel';
